/*
 * Carousel behaviour for converted Replo sections.
 *
 * Replo's runtime drove these; the conversion removes it, which left the nav
 * buttons as dead markup. This reproduces the contract its own DOM already
 * declares, so no markup changes are needed:
 *
 *   [data-replo-part="slides"]        viewport (overflow clipped)
 *     [data-replo-part="slide-track"] the moving track, inline transform
 *       [data-replo-part="slide"]     one per slide
 *   button[data-replo-part="nav-button"][data-direction="next"|"previous"]
 *
 * Nav buttons carry no data-replo-root-id, so they are matched to a track by
 * walking up the DOM and taking the nearest one - the same association Replo's
 * own layout implies (measured at 2 hops on the AIR page).
 *
 * Step size is measured from the rendered slides rather than assumed, so it
 * stays correct across breakpoints and for multi-slide-per-page carousels.
 * Verified against live: a next click moves the AIR reviews track from
 * -2047.5px to -2320.5px, a 273px step.
 */
(function () {
  'use strict';

  var TRACK = '[data-replo-part="slide-track"]';
  var SLIDE = '[data-replo-part="slide"]';
  var NAV = 'button[data-replo-part="nav-button"], [data-replo-part="nav-button"]';

  function trackFor(button) {
    var node = button;
    for (var i = 0; i < 8 && node; i++) {
      node = node.parentElement;
      if (!node) break;
      var t = node.querySelector(TRACK);
      if (t) return t;
    }
    return null;
  }

  function slidesOf(track) {
    return [].filter.call(track.children, function (el) {
      return el.matches && el.matches(SLIDE);
    });
  }

  // One "page" of movement: slide width plus the gap Replo puts between slides.
  function stepFor(track, slides) {
    if (slides.length < 1) return 0;
    if (slides.length > 1) {
      var a = slides[0].getBoundingClientRect();
      var b = slides[1].getBoundingClientRect();
      var delta = Math.abs(b.left - a.left);
      if (delta > 1) return delta;
    }
    return slides[0].getBoundingClientRect().width;
  }

  function perPage(track) {
    var raw = getComputedStyle(track).getPropertyValue('--replo-carousel-slides-per-page');
    var n = parseFloat(raw);
    return n > 0 ? Math.round(n) : 1;
  }

  function currentOffset(track) {
    // read the live transform rather than a stored index, so the starting
    // position baked into the markup is respected
    var m = getComputedStyle(track).transform;
    if (!m || m === 'none') return 0;
    var parts = m.match(/matrix\(([^)]+)\)/);
    if (parts) {
      var v = parts[1].split(',');
      return parseFloat(v[4]) || 0;
    }
    var m3 = m.match(/matrix3d\(([^)]+)\)/);
    if (m3) {
      var v3 = m3[1].split(',');
      return parseFloat(v3[12]) || 0;
    }
    return 0;
  }

  function apply(track, offset) {
    track.style.transform = 'translate3d(' + offset + 'px, 0px, 0px)';
  }

  /*
   * Replo's runtime parked each track at a different position per breakpoint, and the
   * conversion captured those positions into the section stylesheet. They arrive here as
   * `--replo-carousel-offset`, NOT as a transform, and that matters:
   *
   *   .airs3-track-0 { transform: translate3d(-2230px, 0px, 0px) !important; }
   *
   * is what they used to be, and `!important` outranks the inline transform this file writes.
   * Every arrow click below 1025px was computed correctly, written to the element, and then
   * overruled by the stylesheet - so the carousel looked stone dead at tablet and mobile while
   * desktop, which has no such rule, worked perfectly. If a converted section ever gains a
   * baked `transform: ... !important` on a track again, that is the bug.
   *
   * Applied once per breakpoint value: re-applying on every resize would yank the customer
   * back to the start mid-browse, and never applying it means a page loaded at desktop keeps
   * the desktop position after a resize down.
   */
  function applyBakedOffset(track) {
    var raw = getComputedStyle(track).getPropertyValue('--replo-carousel-offset');
    if (!raw) return;
    var value = parseFloat(raw);
    if (isNaN(value)) return;
    if (track.dataset.carouselOffsetApplied === String(value)) return;
    track.dataset.carouselOffsetApplied = String(value);
    apply(track, value);
  }

  function move(track, direction) {
    var slides = slidesOf(track);
    if (!slides.length) return;
    var step = stepFor(track, slides);
    if (!step) return;

    var pages = Math.max(1, perPage(track));
    var maxIndex = Math.max(0, slides.length - pages);
    var minOffset = -(maxIndex * step);

    var offset = currentOffset(track);
    offset += direction === 'next' ? -step : step;

    // clamp rather than wrap: the live carousel stops at both ends
    if (offset > 0) offset = 0;
    if (offset < minOffset) offset = minOffset;

    apply(track, offset);
    syncDisabled(track);
  }

  // Replo shows the unavailable direction with cursor:default. Mirror that so
  // the control still reads as disabled at the ends.
  function syncDisabled(track) {
    var group = track.closest('[data-replo-component-root="carousel"]');
    var scope = group ? group.parentElement || group : document;
    var slides = slidesOf(track);
    var step = stepFor(track, slides);
    var pages = Math.max(1, perPage(track));
    var minOffset = -Math.max(0, slides.length - pages) * step;
    var offset = currentOffset(track);

    [].forEach.call(scope.querySelectorAll(NAV), function (btn) {
      if (trackFor(btn) !== track) return;
      var dir = btn.getAttribute('data-direction');
      var atEnd = dir === 'next' ? offset <= minOffset + 0.5 : offset >= -0.5;
      btn.style.cursor = atEnd ? 'default' : 'pointer';
      if (atEnd) btn.setAttribute('aria-disabled', 'true');
      else btn.removeAttribute('aria-disabled');
    });
  }

  /*
   * Drag and swipe.
   *
   * ⚠ This is NOT parity work, it is a deliberate addition. Below 641px the arrows are hidden
   * by CSS on the LIVE site as well, there are no dots, and a real browser-level touch drag
   * (verified with CDP Input.dispatchTouchEvent, not synthetic events) moves nothing on live
   * either — so on a phone the live carousel cannot be advanced at all. Justyn asked for it to
   * work on mobile, so the gesture is new behaviour. Nothing about the desktop arrows changes.
   *
   * Three details that matter:
   *
   * - Movement snaps in WHOLE SLIDES from where the drag started, never to an absolute grid.
   *   The design parks the track off a slide boundary on purpose so neighbours peek in, and
   *   snapping to multiples of the step would yank that alignment away on first touch.
   * - The gesture only claims the pointer once it has travelled 8px and is more horizontal
   *   than vertical. Otherwise a tap on a play badge, or a vertical page scroll started on top
   *   of a slide, would be swallowed.
   * - A completed drag suppresses the click that follows it, in the capture phase. Without
   *   that, letting go of a swipe fires whatever was under the finger.
   */
  var DRAG_THRESHOLD = 8;

  function bindDrag(track) {
    if (track.dataset.carouselDragBound === 'true') return;
    track.dataset.carouselDragBound = 'true';

    // let the browser keep vertical scrolling, and give us the horizontal axis
    track.style.touchAction = 'pan-y';

    var startX = 0, startY = 0, startOffset = 0, active = false, dragging = false, moved = 0;

    function begin(x, y) {
      active = true; dragging = false; moved = 0;
      startX = x; startY = y;
      startOffset = currentOffset(track);
    }

    // Returns true once the gesture has been claimed, so the touch handler knows to call
    // preventDefault and keep the browser's own scrolling out of it.
    function drag(x, y) {
      if (!active) return false;
      var dx = x - startX, dy = y - startY;
      if (!dragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return false;
        dragging = true;
        track.style.transition = 'none';
      }
      moved = dx;

      var slides = slidesOf(track);
      var step = stepFor(track, slides);
      var pages = Math.max(1, perPage(track));
      var minOffset = -Math.max(0, slides.length - pages) * step;
      apply(track, Math.min(0, Math.max(minOffset, startOffset + dx)));
      return true;
    }

    function finish() {
      if (!active) return;
      active = false;
      if (!dragging) return;
      dragging = false;
      track.style.transition = '';

      var slides = slidesOf(track);
      var step = stepFor(track, slides);
      if (!step) return;
      var pages = Math.max(1, perPage(track));
      var minOffset = -Math.max(0, slides.length - pages) * step;

      // whole slides from the drag's starting point, so the design's phase survives
      var slidesMoved = Math.round(moved / step);
      if (slidesMoved === 0 && Math.abs(moved) > step * 0.25) slidesMoved = moved > 0 ? 1 : -1;
      apply(track, Math.min(0, Math.max(minOffset, startOffset + slidesMoved * step)));
      syncDisabled(track);

      if (Math.abs(moved) >= DRAG_THRESHOLD) {
        track.dataset.carouselJustDragged = '1';
        setTimeout(function () { delete track.dataset.carouselJustDragged; }, 0);
      }
    }

    /*
     * ⚠ TOUCH NEEDS ITS OWN LISTENERS. Pointer events alone are NOT enough, and this cost a
     * round of "it works in the harness and does nothing on a phone".
     *
     * Synthetic PointerEvents dispatched by a test arrive exactly as written, so a
     * pointerdown/move/up implementation passes an in-page assertion. A real finger does not
     * behave that way: the browser owns the gesture until it decides what it is, and it can
     * hand it to its own scrolling and fire `pointercancel` instead of the moves this code was
     * waiting for. `touchmove` is cancelable in a non-passive listener, so claiming the gesture
     * with preventDefault is the only reliable way to keep it.
     *
     * So: touch* drives touch, pointer* drives mouse and pen ONLY (pointerType is checked, or
     * a phone would run both paths for one finger). Verify this path with real input -
     * CDP Input.dispatchTouchEvent on a device context - never with dispatchEvent.
     */
    function notTouch(e) { return e.pointerType !== 'touch'; }

    track.addEventListener('pointerdown', function (e) {
      if (!notTouch(e) || (e.button !== undefined && e.button !== 0)) return;
      begin(e.clientX, e.clientY);
    });
    track.addEventListener('pointermove', function (e) {
      if (!notTouch(e)) return;
      if (drag(e.clientX, e.clientY)) e.preventDefault();
    }, { passive: false });
    track.addEventListener('pointerup', function (e) { if (notTouch(e)) finish(); });
    track.addEventListener('pointercancel', function (e) { if (notTouch(e)) finish(); });

    track.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { active = false; return; }
      begin(e.touches[0].clientX, e.touches[0].clientY);
    }, { passive: true });
    track.addEventListener('touchmove', function (e) {
      if (!e.touches.length) return;
      if (drag(e.touches[0].clientX, e.touches[0].clientY) && e.cancelable) e.preventDefault();
    }, { passive: false });
    track.addEventListener('touchend', finish);
    track.addEventListener('touchcancel', finish);

    /*
     * ⚠ Slides are <img>, and a mouse drag on an image starts the browser's own
     * drag-and-drop. Chromium then stops sending pointermove, so the gesture dies one frame in
     * and the track never moves. Touch is unaffected, which is why this only showed up with a
     * mouse: the local harness swipes by touch and passed while the real page did nothing.
     */
    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    // a swipe must not also activate whatever it finished on top of
    track.addEventListener('click', function (e) {
      if (track.dataset.carouselJustDragged !== '1') return;
      e.preventDefault();
      e.stopPropagation();
    }, true);
  }

  function bind() {
    document.querySelectorAll(NAV).forEach(function (btn) {
      if (btn.dataset.carouselBound === 'true') return;
      var track = trackFor(btn);
      if (!track) return;
      btn.dataset.carouselBound = 'true';

      btn.addEventListener('click', function (e) {
        e.preventDefault();
        move(track, btn.getAttribute('data-direction') === 'next' ? 'next' : 'previous');
      });
    });

    // put each track where this breakpoint says it starts, then set the initial
    // disabled state without moving anything further
    document.querySelectorAll(TRACK).forEach(function (t) {
      applyBakedOffset(t);
      bindDrag(t);
      if (t.dataset.carouselInit === 'true') return;
      t.dataset.carouselInit = 'true';
      syncDisabled(t);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }

  // slide widths change with the viewport, and the theme editor re-renders
  // sections without a page load
  window.addEventListener('resize', function () {
    document.querySelectorAll(TRACK).forEach(function (t) {
      applyBakedOffset(t);   // no-op unless the breakpoint actually changed
      syncDisabled(t);
    });
  });
  document.addEventListener('shopify:section:load', bind);
})();
